import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { speechRecognitionLang } from '../../constants/chatLanguages';
import {
  CAPTION_ACTIONS,
  captionActionLabel,
  loadCaptionRules,
  normalizeCaptionAliasList,
  saveCaptionRules,
} from '../../utils/captionSettings';

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const emptyDraft = () => ({
  phrase: '',
  aliases: '',
  action: 'create_task',
});

const CaptionSettingsModal = ({ open, onClose }) => {
  const preferredLanguage = useSelector((state) => state.auth?.user?.preferredLanguage || 'en');
  const [rules, setRules] = useState(() => loadCaptionRules());
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [recordingField, setRecordingField] = useState('');
  const recRef = useRef(null);

  const stopRecording = () => {
    try {
      recRef.current?.stop?.();
    } catch {
      // already stopped
    }
    recRef.current = null;
    setRecordingField('');
  };

  useEffect(() => {
    if (!open) {
      stopRecording();
      return undefined;
    }
    setRules(loadCaptionRules());
    setDraft(emptyDraft());
    setEditingId(null);
    return () => stopRecording();
  }, [open]);

  const persist = (next) => {
    setRules(saveCaptionRules(next));
  };

  const defaults = useMemo(() => rules.filter((rule) => rule.builtin), [rules]);
  const custom = useMemo(() => rules.filter((rule) => !rule.builtin), [rules]);

  const applyRecordedText = (text, field) => {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value) return;
    setDraft((prev) => {
      if (field !== 'aliases') return { ...prev, phrase: value };
      const lines = String(prev.aliases || '').split('\n');
      if (!lines.length) return { ...prev, aliases: value };
      lines[lines.length - 1] = value;
      return { ...prev, aliases: lines.join('\n') };
    });
  };

  const toggleRecord = (field) => {
    if (recordingField === field) {
      stopRecording();
      return;
    }
    if (!SpeechRecognitionAPI) {
      window.alert('Speech recognition is not available in this browser. Type the caption instead.');
      return;
    }
    stopRecording();
    const rec = new SpeechRecognitionAPI();
    rec.lang = speechRecognitionLang(preferredLanguage);
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (event) => {
      const text = Array.from(event.results || [])
        .map((result) => result?.[0]?.transcript || '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      applyRecordedText(text, field);
    };
    rec.onerror = () => stopRecording();
    rec.onend = () => {
      if (recRef.current === rec) setRecordingField('');
    };
    recRef.current = rec;
    try {
      rec.start();
      setRecordingField(field);
    } catch {
      stopRecording();
    }
  };

  if (!open) return null;

  const submitDraft = () => {
    stopRecording();
    const phrase = draft.phrase.replace(/\s+/g, ' ').trim();
    if (!phrase) return;
    const aliases = normalizeCaptionAliasList(draft.aliases, phrase);
    if (editingId) {
      persist(rules.map((rule) => (
        rule.id === editingId ? { ...rule, phrase, aliases, action: draft.action } : rule
      )));
    } else {
      persist([
        ...rules,
        {
          id: `cap-${Date.now()}`,
          phrase,
          aliases,
          action: draft.action,
          enabled: true,
          builtin: false,
        },
      ]);
    }
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const RecordButton = ({ field }) => {
    const active = recordingField === field;
    return (
      <button
        type="button"
        onClick={() => toggleRecord(field)}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${
          active
            ? 'border-rose-400 bg-rose-50 text-rose-600 dark:border-rose-500 dark:bg-rose-500/15 dark:text-rose-300'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
        }`}
        aria-label={active ? 'Stop recording' : 'Record caption'}
        title={active ? 'Stop recording' : 'Record caption'}
      >
        {active ? (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <rect x="6" y="6" width="8" height="8" rx="1.5" />
          </svg>
        ) : (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10v2a7 7 0 01-14 0v-2M12 19v2" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Caption settings"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Caption settings</h2>
        <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
          Record in any language — the transcript fills the box. Add other possible cases so we can match shorter, reordered, or translated phrases too.
        </p>

        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Caption text</p>
          <div className="mt-1.5 flex items-start gap-2">
            <input
              value={draft.phrase}
              onChange={(event) => setDraft((prev) => ({ ...prev, phrase: event.target.value }))}
              placeholder="e.g. create me a reminder"
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
            <RecordButton field="phrase" />
          </div>
          {recordingField === 'phrase' ? (
            <p className="mt-1 text-[11px] font-medium text-rose-600">Listening… speak the caption.</p>
          ) : null}

          <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Other possible cases
          </p>
          <div className="mt-1.5 flex items-start gap-2">
            <textarea
              value={draft.aliases}
              onChange={(event) => setDraft((prev) => ({ ...prev, aliases: event.target.value }))}
              placeholder={'One permutation per line\ncreate a reminder\nmake me a reminder\nmujhe reminder banao'}
              rows={4}
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
            <RecordButton field="aliases" />
          </div>
          <p className="mt-1 text-[11px] text-gray-400">
            Add permutations and combinations you might actually say: short form, different word order, slang, or another language.
          </p>
          {recordingField === 'aliases' ? (
            <p className="mt-1 text-[11px] font-medium text-rose-600">Listening… this line is filled from speech.</p>
          ) : null}

          <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Action</p>
          <select
            value={draft.action}
            onChange={(event) => setDraft((prev) => ({ ...prev, action: event.target.value }))}
            className="mt-1.5 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          >
            {CAPTION_ACTIONS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-400">
            {CAPTION_ACTIONS.find((item) => item.id === draft.action)?.hint}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            {editingId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setDraft(emptyDraft());
                }}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700"
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              onClick={submitDraft}
              disabled={!draft.phrase.trim()}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
            >
              {editingId ? 'Update' : 'Add setup'}
            </button>
          </div>
        </div>

        <section className="mt-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Default setup</h3>
          <ul className="mt-2 space-y-1.5">
            {defaults.map((rule) => (
              <li
                key={rule.id}
                className="flex items-start gap-2 rounded-lg border border-gray-100 dark:border-gray-800 px-2.5 py-2"
              >
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(event) => persist(rules.map((item) => (
                    item.id === rule.id ? { ...item, enabled: event.target.checked } : item
                  )))}
                  className="mt-1"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    “{rule.phrase}”
                  </p>
                  <p className="text-[11px] text-gray-500">{captionActionLabel(rule.action)}</p>
                  {rule.aliases?.length ? (
                    <p className="text-[11px] text-gray-400 truncate">Also: {rule.aliases.join(' · ')}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(rule.id);
                    setDraft({
                      phrase: rule.phrase,
                      aliases: (rule.aliases || []).join('\n'),
                      action: rule.action,
                    });
                  }}
                  className="text-[11px] font-semibold text-primary-600"
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your setups</h3>
          {custom.length === 0 ? (
            <p className="mt-2 text-[12px] text-gray-400">No custom phrases yet.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {custom.map((rule) => (
                <li
                  key={rule.id}
                  className="flex items-start gap-2 rounded-lg border border-gray-100 dark:border-gray-800 px-2.5 py-2"
                >
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(event) => persist(rules.map((item) => (
                      item.id === rule.id ? { ...item, enabled: event.target.checked } : item
                    )))}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      “{rule.phrase}”
                    </p>
                    <p className="text-[11px] text-gray-500">{captionActionLabel(rule.action)}</p>
                    {rule.aliases?.length ? (
                      <p className="text-[11px] text-gray-400 truncate">Also: {rule.aliases.join(' · ')}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(rule.id);
                      setDraft({
                        phrase: rule.phrase,
                        aliases: (rule.aliases || []).join('\n'),
                        action: rule.action,
                      });
                    }}
                    className="text-[11px] font-semibold text-primary-600"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => persist(rules.filter((item) => item.id !== rule.id))}
                    className="text-gray-400 hover:text-rose-500 px-1"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => {
              stopRecording();
              onClose();
            }}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaptionSettingsModal;
